// @flow

import flatten from 'lodash/flatten';
import lodashGet from 'lodash/get';

import {
  createErrorHandler,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {limitRequests} from 'app/utils/redux';
import {getDenormalizedListFactory} from 'app/utils/redux_v2';
import {gt, ngt} from 'app/utils/i18n';
import type {
  TagsDashboardFetchParamsType,
  TagsDashboardLocationParamsType,
  ObjectToTag,
  TaggedItemType,
  TagType,
} from 'app/tags/types';
import {
  attachTagsFetcher,
  detachTagFetcher,
  tagsListFetcher,
} from 'app/tags/fetchers';

import {type ResponseType} from 'app/utils/redux_v2';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {showNotification} from 'app/toasts/slice';
import * as request from 'app/utils/request';

export const convertLocationParamsToFetchParams = (
  query: TagsDashboardLocationParamsType
): TagsDashboardFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  search: query.search,
  slug: query.slug,
  exclude_slug: query.exclude_slug,
});

export const denormalizeTagsList = getDenormalizedListFactory<TagType>(
  (state) => state.tagsArea
);

/* fetch functions */

export const REQUEST_TAG_LIST = 'REQUEST_TAG_LIST';

export const requestTagList = () => ({
  type: REQUEST_TAG_LIST,
});

export const RECEIVE_TAG_LIST = 'RECEIVE_TAG_LIST';

export const receiveTagsList = (
  query: TagsDashboardFetchParamsType,
  response: ResponseType<Process>
) => ({
  type: RECEIVE_TAG_LIST,
  query,
  response,
});

export const fetchTags = limitRequests(
  (fetchParams: TagsDashboardFetchParamsType) => (dispatch: SimpleDispatch) => {
    dispatch(requestTagList());

    return tagsListFetcher(fetchParams).then(
      (response) => dispatch(receiveTagsList(fetchParams, response)),
      createErrorHandler(gt('Unable to load the tags'), dispatch)
    );
  }
);

/* attach/detach tags */

export const attachTags =
  (
    documents: ObjectToTag | Array<ObjectToTag>,
    tagsNames: string | Array<string>
  ) =>
  (dispatch: FuncDispatch) => {
    // convert single objects params to arrays
    const docs = Array.isArray(documents) ? documents : [documents];
    const tags = Array.isArray(tagsNames) ? tagsNames : [tagsNames];

    const taggedItems: Array<TaggedItemType> = flatten(
      tags.map((name) =>
        docs.map((doc) => ({
          content_type: doc.content_type,
          object_id: doc.id,
          name,
        }))
      )
    );

    return attachTagsFetcher(taggedItems)
      .then((response) => {
        dispatch(showNotification(ngt('Tag added', 'Tags added', tags.length)));
        return response;
      })
      .catch(
        request.createErrorHandler(
          ngt('Unable to add tag', 'Unable to add tags', tags.length),
          dispatch,
          {
            '400': (error, dispatch) =>
              dispatch(
                showNotification(
                  lodashGet(
                    error,
                    'data[0].name[0]',
                    ngt('Unable to add tag', 'Unable to add tags', tags.length)
                  ),
                  'error'
                )
              ),
          }
        )
      );
  };

export const detachTag = (taggedItemURL: string) => (dispatch: FuncDispatch) =>
  detachTagFetcher(taggedItemURL)
    .then((response) => {
      dispatch(showNotification(gt('Tag removed')));
      return response;
    })
    .catch(
      request.createErrorHandler(
        gt('Unable to remove tag'),
        dispatch,
        showForbiddenMessageHandler(false)
      )
    );

/* dashboard */

export const SET_TAGS_DASHBOARD_FILTERS = 'SET_TAGS_DASHBOARD_FILTERS';

export const setTagsDashboardFilters = (
  filters: TagsDashboardLocationParamsType
) => ({
  type: SET_TAGS_DASHBOARD_FILTERS,
  filters: {...filters},
});

export const fetchTagsForDashboard = limitRequests(
  (locationParams: TagsDashboardLocationParamsType) =>
    (dispatch: SimpleDispatch): Promise<any> => {
      dispatch(setTagsDashboardFilters(locationParams));
      const fetchParams = convertLocationParamsToFetchParams(locationParams);
      return ((dispatch: any): FuncDispatch)(fetchTags(fetchParams));
    }
);
